import * as React from "react"
import { cn } from "@/lib/utils"

/** Bare-bones live audio visualizer.
 *
 *  When ``active`` flips to true we request mic permission, hand the
 *  resulting ``MediaStream`` to ``onStreamReady`` (so callers can
 *  attach a ``MediaRecorder`` to the same stream — getting two
 *  permission prompts looks broken to users), and start drawing
 *  frequency-bin bars on the canvas.
 *
 *  When ``active`` is false but ``processing`` is true, we draw a
 *  gentle idle wave so the UI doesn't go blank during the
 *  transcription round-trip.
 *
 *  Inspired by elevenlabs/ui's ``LiveWaveform``; reimplemented locally
 *  to avoid the extra dependency. The two visible modes are:
 *  - ``static`` (default) — symmetric frequency bars, like a quick
 *    spectrogram snapshot.
 *  - ``scrolling`` — average-volume history scrolling right→left.
 */

export interface LiveWaveformProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "onError"> {
  active?: boolean
  processing?: boolean
  mode?: "static" | "scrolling"
  height?: number
  barWidth?: number
  barGap?: number
  barRadius?: number
  /** Tailwind-style colour string for the bars. Defaults to ``currentColor``. */
  barColor?: string
  /** Smoothing constant for the AnalyserNode. 0–1, higher = smoother. */
  smoothingTimeConstant?: number
  fftSize?: number
  /** History length for ``scrolling`` mode. */
  historySize?: number
  /** Optional pre-acquired ``MediaStream``. When supplied, the
   *  waveform builds its AnalyserNode from this stream and does NOT
   *  call ``getUserMedia`` — important when a recorder already owns
   *  the mic, since opening a second stream would prompt for
   *  permission twice and double the device's "in use" indicators. */
  stream?: MediaStream | null
  onStreamReady?: (stream: MediaStream) => void
  onStreamEnd?: () => void
  onError?: (error: Error) => void
}

export function LiveWaveform({
  active = false,
  processing = false,
  mode = "static",
  height = 48,
  barWidth = 3,
  barGap = 2,
  barRadius = 1.5,
  barColor,
  smoothingTimeConstant = 0.8,
  fftSize = 256,
  historySize = 64,
  stream: externalStream,
  onStreamReady,
  onStreamEnd,
  onError,
  className,
  ...divProps
}: LiveWaveformProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const audioCtxRef = React.useRef<AudioContext | null>(null)
  const analyserRef = React.useRef<AnalyserNode | null>(null)
  const sourceRef = React.useRef<MediaStreamAudioSourceNode | null>(null)
  const streamRef = React.useRef<MediaStream | null>(null)
  const rafRef = React.useRef<number | null>(null)
  const historyRef = React.useRef<number[]>([])

  // Latest callbacks held in refs so the audio-setup effect can read
  // them without re-running every time the parent re-renders with a
  // new closure — which would otherwise tear down + re-create the
  // mic stream and prompt the user again.
  const onStreamReadyRef = React.useRef(onStreamReady)
  const onStreamEndRef = React.useRef(onStreamEnd)
  const onErrorRef = React.useRef(onError)
  React.useEffect(() => {
    onStreamReadyRef.current = onStreamReady
  }, [onStreamReady])
  React.useEffect(() => {
    onStreamEndRef.current = onStreamEnd
  }, [onStreamEnd])
  React.useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  // Mic acquisition + analyser setup. ``active`` re-runs this effect;
  // a parent-supplied ``externalStream`` reference also re-runs it so
  // the analyser binds to the right stream when the recorder swaps
  // tracks. We deliberately *don't* depend on fft/smoothing — the
  // caller should remount if they change.
  React.useEffect(() => {
    if (!active) return
    let cancelled = false

    // Track whether the cleanup should stop the tracks. We only own
    // the stream when we acquired it ourselves; an externalStream
    // belongs to the caller.
    let ownsStream = false

    const setup = (stream: MediaStream) => {
      if (cancelled) {
        if (ownsStream) stream.getTracks().forEach((t) => t.stop())
        return
      }
      streamRef.current = stream
      if (ownsStream) onStreamReadyRef.current?.(stream)

      const Ctor: typeof AudioContext =
        window.AudioContext ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).webkitAudioContext
      const ctx = new Ctor()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = fftSize
      analyser.smoothingTimeConstant = smoothingTimeConstant
      source.connect(analyser)

      audioCtxRef.current = ctx
      analyserRef.current = analyser
      sourceRef.current = source
      historyRef.current = []
    }

    if (externalStream) {
      // Caller already owns the stream — skip getUserMedia entirely
      // so we don't open a second mic.
      setup(externalStream)
    } else {
      const mediaDevices = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined
      if (!mediaDevices) {
        onErrorRef.current?.(new Error("Microphone access is not available in this browser."))
        return
      }
      ownsStream = true
      mediaDevices
        .getUserMedia({ audio: true })
        .then(setup)
        .catch((err: Error) => {
          if (!cancelled) onErrorRef.current?.(err)
        })
    }

    return () => {
      cancelled = true
      try {
        sourceRef.current?.disconnect()
      } catch {
        // ignore
      }
      sourceRef.current = null
      analyserRef.current = null
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        void audioCtxRef.current.close().catch(() => undefined)
      }
      audioCtxRef.current = null
      // Only stop tracks we acquired ourselves; an externalStream
      // is the caller's responsibility.
      if (ownsStream) {
        streamRef.current?.getTracks().forEach((t) => t.stop())
        onStreamEndRef.current?.()
      }
      streamRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, externalStream])

  // Render loop — runs while either active (real frequency data) or
  // processing (idle wave) so we always have *something* on screen
  // during the recorder's loading state.
  React.useEffect(() => {
    if (!active && !processing) return
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    let phase = 0
    const draw = () => {
      rafRef.current = requestAnimationFrame(draw)

      const dpr = window.devicePixelRatio || 1
      const cssWidth = container.clientWidth
      const cssHeight = height
      if (canvas.width !== cssWidth * dpr || canvas.height !== cssHeight * dpr) {
        canvas.width = cssWidth * dpr
        canvas.height = cssHeight * dpr
        canvas.style.width = `${cssWidth}px`
        canvas.style.height = `${cssHeight}px`
      }

      const ctx = canvas.getContext("2d")
      if (!ctx) return
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, cssWidth, cssHeight)
      ctx.fillStyle = barColor ?? "currentColor"

      const slot = barWidth + barGap
      const slotCount = Math.max(1, Math.floor(cssWidth / slot))
      const offsetX = (cssWidth - (slotCount * slot - barGap)) / 2
      const midY = cssHeight / 2

      const analyser = analyserRef.current
      if (active && analyser) {
        const buf = new Uint8Array(analyser.frequencyBinCount)
        analyser.getByteFrequencyData(buf)

        if (mode === "scrolling") {
          // One sample per frame — sum a chunk so silence reads as ~0.
          const sample = sampleAverage(buf)
          historyRef.current.push(sample)
          if (historyRef.current.length > historySize) historyRef.current.shift()
          drawScrolling(ctx, historyRef.current, slotCount, slot, offsetX, midY, cssHeight, barWidth, barRadius)
        } else {
          drawStatic(ctx, buf, slotCount, slot, offsetX, midY, cssHeight, barWidth, barRadius)
        }
      } else {
        // Idle / processing — gentle sine-ish pulse so the UI doesn't
        // appear frozen between stop-click and transcript-arrived.
        phase = (phase + 0.06) % (Math.PI * 2)
        for (let i = 0; i < slotCount; i++) {
          const x = offsetX + i * slot
          const t = i / slotCount
          const wave = Math.sin(phase + t * Math.PI * 2) * 0.5 + 0.5
          const h = Math.max(2, wave * cssHeight * 0.45)
          drawBar(ctx, x, midY - h / 2, barWidth, h, barRadius)
        }
      }
    }

    draw()
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [active, processing, mode, height, barWidth, barGap, barRadius, barColor, historySize])

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full overflow-hidden", className)}
      style={{ height }}
      {...divProps}
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  )
}

function sampleAverage(buf: Uint8Array): number {
  if (buf.length === 0) return 0
  let total = 0
  for (let i = 0; i < buf.length; i++) total += buf[i]
  return total / buf.length / 255
}

function drawBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  // Modern browsers all have ``roundRect``; fall back gracefully.
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, radius)
  } else {
    ctx.rect(x, y, w, h)
  }
  ctx.fill()
}

function drawStatic(
  ctx: CanvasRenderingContext2D,
  buf: Uint8Array,
  slotCount: number,
  slot: number,
  offsetX: number,
  midY: number,
  totalHeight: number,
  barWidth: number,
  barRadius: number,
) {
  // Mirror the lower half of the FFT bins around center for that
  // classic "voice equalizer" look.
  const halfBars = Math.ceil(slotCount / 2)
  for (let i = 0; i < halfBars; i++) {
    const binIdx = Math.floor((i / halfBars) * (buf.length * 0.6))
    const v = buf[binIdx] / 255
    const h = Math.max(2, v * totalHeight)
    const right = offsetX + (halfBars + i) * slot
    const left = offsetX + (halfBars - 1 - i) * slot
    drawBar(ctx, right, midY - h / 2, barWidth, h, barRadius)
    drawBar(ctx, left, midY - h / 2, barWidth, h, barRadius)
  }
}

function drawScrolling(
  ctx: CanvasRenderingContext2D,
  history: number[],
  slotCount: number,
  slot: number,
  offsetX: number,
  midY: number,
  totalHeight: number,
  barWidth: number,
  barRadius: number,
) {
  // Newest sample on the right; older samples shift left and fade
  // out by virtue of the ring buffer.
  const start = Math.max(0, history.length - slotCount)
  for (let i = start; i < history.length; i++) {
    const v = history[i]
    const h = Math.max(2, v * totalHeight * 0.9)
    const x = offsetX + (slotCount - (history.length - i)) * slot
    drawBar(ctx, x, midY - h / 2, barWidth, h, barRadius)
  }
}
